import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { Registration } from '../../types/registration';
import './PaymentPage.css';

const PaymentPage: React.FC = () => {
  const { registrationId } = useParams<{ registrationId: string }>();
  const navigate = useNavigate();
  
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [balance, setBalance] = useState<number>(0);

  useEffect(() => {
    const fetchRegistration = async () => {
      if (!registrationId) {
        setError('Registration ID is required');
        setLoading(false);
        return;
      }

      try {
        const registrationDoc = await getDoc(doc(db, 'Registrations', registrationId));
        
        if (!registrationDoc.exists()) {
          setError('Registration not found');
          setLoading(false);
          return;
        }

        const regData = { id: registrationDoc.id, ...registrationDoc.data() } as Registration;
        
        // Check if registration is cancelled
        if (regData.status === 'Cancelled' || regData.status === 'cancelled') {
          setError('This registration has been cancelled');
          setLoading(false);
          return;
        }

        setRegistration(regData);

        // Calculate balance
        const totalFees = calculateTotalFees(regData);
        const totalPayments = (regData.paymentHistory || []).reduce(
          (sum, payment) => sum + (Number(payment.amount) || 0),
          0
        );
        const calculatedBalance = totalFees - totalPayments;
        const finalBalance = Math.max(0, calculatedBalance);
        
        setBalance(finalBalance);
        setPaymentAmount(finalBalance.toFixed(2));
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching registration:', err);
        setError(err.message || 'Failed to load registration');
        setLoading(false);
      }
    };

    fetchRegistration();
  }, [registrationId]);

  const calculateTotalFees = (reg: Registration): number => {
    const fin = reg.financial as any;
    if (!fin) return 0;

    const accommodationFee = Number(fin.accommodationFees?.accommodationFee) || 0;
    const accommodationOverride = fin.overrides?.accommodation?.enabled
      ? (Number(fin.overrides.accommodation.amount) || 0) : 0;

    let registrationBase = 0;
    for (const cat of ['adults', 'adultStudents', 'youthChildren']) {
      for (const camper of (fin.registrationFees?.[cat] || [])) {
        registrationBase += Number(camper.fee) || 0;
      }
    }
    const registrationOverride = fin.overrides?.registration?.enabled
      ? (Number(fin.overrides.registration.amount) || 0) : 0;

    const meals = fin.otherFees?.meals;
    const mealBase =
      ((meals?.fullCampPassesAdult?.quantity || 0) * (meals?.fullCampPassesAdult?.unitPrice || 0)) +
      ((meals?.fullCampPassesChild?.quantity || 0) * (meals?.fullCampPassesChild?.unitPrice || 0)) +
      ((meals?.dailyPassesAdult?.quantity || 0) * (meals?.dailyPassesAdult?.unitPrice || 0)) +
      ((meals?.dailyPassesChild?.quantity || 0) * (meals?.dailyPassesChild?.unitPrice || 0));
    const mealOverride = fin.overrides?.mealFees?.enabled
      ? (Number(fin.overrides.mealFees.amount) || 0) : 0;

    const fridgeRental = Number(fin.otherFees?.fridge?.cost) || 0;
    const donation = Number(fin.otherFees?.donations?.amount) || 0;
    const customFee = Number(fin.otherFees?.customFee?.amount) || 0;
    const otherFeesArray = (fin.otherFees?.otherFees || []).reduce(
      (sum: number, f: any) => sum + (Number(f.amount) || 0), 0
    );
    const otherOverride = fin.overrides?.otherFees?.enabled
      ? (Number(fin.overrides.otherFees.amount) || 0) : 0;

    const total = accommodationFee + accommodationOverride +
      registrationBase + registrationOverride +
      mealBase + mealOverride +
      fridgeRental + donation + customFee + otherFeesArray + otherOverride;

    console.log('PaymentPage fee calc v2:', {
      accommodationFee, accommodationOverride,
      registrationBase, registrationOverride,
      mealBase, mealOverride,
      fridgeRental, donation, customFee, otherFeesArray, otherOverride,
      total,
      rawRegistrationFees: JSON.stringify(fin.registrationFees)
    });

    return total;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numbers and one decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setPaymentAmount(value);
    }
  };

  const calculateConvenienceFee = (amount: number): number => {
    return Math.round(amount * 0.03 * 100) / 100;
  };

  const handlePay = async () => {
    if (isProcessing) return;

    const amount = parseFloat(paymentAmount);
    
    // Validation
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    if (amount < 10) {
      setError('Minimum payment amount for credit card is $10.00');
      return;
    }

    if (amount > balance) {
      setError(`Payment amount cannot exceed balance of $${balance.toFixed(2)}`);
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({
        registrationId,
        paymentAmount: amount,
      });

      const data = result.data as { sessionUrl?: string };
      
      if (data.sessionUrl) {
        // Redirect to Stripe Checkout
        window.location.href = data.sessionUrl;
      } else {
        throw new Error('No session URL returned');
      }
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      console.error('Error details:', JSON.stringify(err, null, 2));
      
      // Handle Firebase Functions HttpsError
      let errorMessage = 'Failed to create payment session. Please try again.';
      
      // Firebase Functions errors have a code and message
      if (err.code) {
        // Extract the actual error message
        if (err.details) {
          errorMessage = err.details;
        } else if (err.message) {
          errorMessage = err.message;
        } else {
          errorMessage = `Error: ${err.code}. Please try again or contact support.`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error && !registration) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="error-message">{error}</div>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="error-message">Registration not found</div>
        </div>
      </div>
    );
  }

  const amount = parseFloat(paymentAmount) || 0;
  const convenienceFee = calculateConvenienceFee(amount);
  const total = amount + convenienceFee;
  const showPayButton = balance >= 10;

  return (
    <div className="payment-page">
      <div className="payment-container">
        <h1>Pay Online</h1>
        
        {registration && (
          <div className="camper-info">
            <p><strong>Camper:</strong> {
              (registration as any).registrationInfo?.primaryCamperName || 
              (registration.firstName && registration.lastName ? `${registration.firstName} ${registration.lastName}` : '') ||
              'N/A'
            }</p>
          </div>
        )}

        {error && (
          <div className="error-message">{error}</div>
        )}

        {balance < 10 && balance > 0 ? (
          <div className="info-message">
            <p>Your balance is less than $10.00. Credit card payments require a minimum of $10.00.</p>
            <p>Please use an alternative payment method (e-transfer or cash/check).</p>
          </div>
        ) : balance === 0 ? (
          <div className="info-message">
            <p>Your account is fully paid. No payment is required.</p>
          </div>
        ) : (
          <>
            <div className="balance-info">
              <p><strong>Current Balance:</strong> ${balance.toFixed(2)} CAD</p>
            </div>

            <div className="payment-form">
              <div className="form-group">
                <label htmlFor="paymentAmount">Payment Amount (CAD)</label>
                <input
                  type="text"
                  id="paymentAmount"
                  value={paymentAmount}
                  onChange={handleAmountChange}
                  disabled={isProcessing}
                  className="form-control"
                />
                <small>Enter amount to pay (minimum $10.00)</small>
              </div>

              <div className="fee-breakdown">
                <div className="fee-row">
                  <span>Payment Amount:</span>
                  <span>${amount.toFixed(2)}</span>
                </div>
                <div className="fee-row">
                  <span>Convenience Fee (3%):</span>
                  <span>${convenienceFee.toFixed(2)}</span>
                </div>
                <div className="fee-row total">
                  <span><strong>Total:</strong></span>
                  <span><strong>${total.toFixed(2)}</strong></span>
                </div>
              </div>

              <div className="info-note">
                <p><small>Credit card payments include a 3% convenience fee. This fee is clearly labeled and only applies to credit card payments.</small></p>
              </div>

              <button
                onClick={handlePay}
                disabled={isProcessing || amount < 10 || amount > balance || amount <= 0}
                className="btn btn-primary btn-pay"
              >
                {isProcessing ? 'Processing...' : 'Pay with Credit Card'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;

